import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Drawer } from 'vaul';
import { Search, X } from 'lucide-react';
import { cx } from './cx.ts';
import { Button } from './Button.tsx';
import { phoneMatches } from '../../shared/phone.ts';

/** Bottom sheet: the main way choices and confirmations appear on a phone. */
export function Sheet({ open, onOpenChange, title, description, children, footer, size = 'auto' }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'auto' | 'tall';
}) {
  useBackToClose(open, () => onOpenChange(false));
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-[rgb(17_18_20/0.45)] backdrop-blur-[2px]" />
        <Drawer.Content
          aria-describedby={undefined}
          className={cx(
            'fixed inset-x-0 bottom-0 z-50 mx-auto flex max-w-[640px] flex-col rounded-t-[28px] bg-surface outline-none',
            'shadow-float',
            size === 'tall' ? 'h-[92dvh]' : 'max-h-[92dvh]',
          )}
        >
          <div className="mx-auto mt-2.5 h-1.5 w-10 shrink-0 rounded-full bg-line-strong" />
          {(title || description) && (
            <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-3">
              <div>
                {title ? <Drawer.Title className="text-[19px] font-bold tracking-[-0.01em] text-ink">{title}</Drawer.Title>
                  : <Drawer.Title className="sr-only">Options</Drawer.Title>}
                {description && <Drawer.Description className="mt-0.5 text-[14px] text-ink-2">{description}</Drawer.Description>}
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className="press hit -mr-1 flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-2 text-ink-2"
              >
                <X className="size-[18px]" />
              </button>
            </div>
          )}
          {!title && <Drawer.Title className="sr-only">Options</Drawer.Title>}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">{children}</div>
          {footer && <div className="safe-bottom border-t border-line px-5 pb-3 pt-3">{footer}</div>}
          {!footer && <div className="safe-bottom" />}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/**
 * On phones, the system Back button should close an open sheet, not leave the
 * screen behind it (and lose a half-entered bill). Opening a sheet adds one
 * history entry; Back pops it and closes the sheet. Closing the sheet any other
 * way removes that entry again, unless the app has moved to another page meanwhile.
 */
function useBackToClose(open: boolean, close: () => void) {
  const closeRef = useRef(close);
  closeRef.current = close;
  // One marker per opening, shared by StrictMode's double-run of the effect.
  const marker = useRef<number | null>(null);
  const active = useRef<number | null>(null);
  if (open && marker.current === null) marker.current = Math.random();
  if (!open) marker.current = null;
  useEffect(() => {
    const m = marker.current;
    if (!open || m === null) return;
    const ours = () => (window.history.state as { lbSheet?: number } | null)?.lbSheet === m;
    active.current = m;
    if (!ours()) window.history.pushState({ ...(window.history.state ?? {}), lbSheet: m }, '');
    const onPop = () => {
      if (!ours()) closeRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      active.current = null;
      // Wait a tick: if the effect simply re-ran (StrictMode), the sheet is still open.
      setTimeout(() => {
        if (active.current !== m && ours()) window.history.back();
      }, 0);
    };
  }, [open]);
}

export function ConfirmSheet({ open, onOpenChange, title, message, confirmLabel, tone = 'primary', onConfirm, loading, children }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={title} description={message}>
      {children}
      <div className="mt-4 flex flex-col gap-2.5">
        <Button size="lg" block variant={tone === 'danger' ? 'out' : 'primary'} loading={loading} onClick={onConfirm}>
          {confirmLabel}
        </Button>
        <Button size="lg" block variant="ghost" onClick={() => onOpenChange(false)}>Keep it</Button>
      </div>
    </Sheet>
  );
}

export interface PickerOption {
  id: string;
  title: string;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  keywords?: string;
  /** Found by number too, however it's typed. */
  phone?: string | null;
}

/** Searchable list in a sheet: customers, items, accounts, vehicles. */
export function PickerSheet({ open, onOpenChange, title, options, onPick, onCreate, createLabel, emptyText, loading, selectedId }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  options: PickerOption[];
  onPick: (id: string) => void;
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => string;
  emptyText?: string;
  loading?: boolean;
  selectedId?: string | null;
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => `${o.title} ${o.keywords ?? ''}`.toLowerCase().includes(needle) || phoneMatches(o.phone, needle));
  }, [q, options]);
  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setQ(''); }} title={title} size="tall">
      <div className="sticky top-0 z-10 -mx-5 bg-surface px-5 pb-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-[18px] -translate-y-1/2 text-ink-3" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            className="h-12 w-full rounded-xl bg-surface-2 pl-10 pr-3 text-[16px] outline-none placeholder:text-ink-3 focus:shadow-[0_0_0_3px_var(--brand-soft)]"
          />
        </div>
      </div>
      {onCreate && (
        <button
          type="button"
          onClick={() => { onCreate(q.trim()); setQ(''); }}
          className="press mb-1 flex w-full items-center gap-3 rounded-2xl px-2 py-3 text-left hover:bg-surface-2"
        >
          <span className="flex size-10 items-center justify-center rounded-full bg-brand text-[22px] font-light leading-none text-brand-ink">+</span>
          <span className="text-[15.5px] font-semibold text-ink">{createLabel ? createLabel(q.trim()) : 'Add new'}</span>
        </button>
      )}
      {loading ? (
        <div className="space-y-2 pt-2">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-14 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="px-2 py-8 text-center text-[14.5px] text-ink-3">{q ? `Nothing matches “${q}”` : emptyText ?? 'Nothing here yet'}</p>
      ) : (
        <ul className="pb-2">
          {filtered.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => { onPick(o.id); setQ(''); }}
                className={cx('press flex w-full items-center gap-3 rounded-2xl px-2 py-2.5 text-left hover:bg-surface-2',
                  selectedId === o.id && 'bg-brand-soft')}
              >
                {o.leading}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15.5px] font-semibold text-ink">{o.title}</span>
                  {o.subtitle && <span className="block truncate text-[13px] text-ink-3">{o.subtitle}</span>}
                </span>
                {o.trailing}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
